import type {ChronologicalCommentsSort} from '@github-ui/commenting/useCommentsSortPreference'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueViewerQueryIdentifier} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {useSuspenseQuery} from '@github-ui/react-query'
import {useRelayCachePreheatMode} from '@github-ui/relay-query-cache/contexts/RelayCachePreheatContext'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {type ReactNode, type RefObject, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import {getInitialTimelinePageSize} from '../../constants/values'
import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import {useIssueHeaderDataContext} from '../header/IssueHeaderDataContext'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import type {
  IssueTimelineIssueFragment$data,
  IssueTimelineIssueFragment$key,
} from './__generated__/IssueTimelineIssueFragment.graphql'
import {type TimelineFeed, type TimelineItemTypes, timelineQueryOptions} from './fetch-timeline-items'
import type {EventProps, IssueTimelineItemFragment} from './IssueTimelineItem'
import type {LoadMoreCallbackFn} from './LoadMore'
import {frontParamsFor} from './timeline-prefill'
import {orderTimelineItems} from './timeline-sort'
import {TimelinePanel} from './TimelinePanel'
import {useTimelineItems} from './use-timeline-items'
import type {UseTimelineItemsCoreResult} from './use-timeline-items-core'
import {useTimelineItemsTanstack} from './use-timeline-items-tanstack'

type LoadItem = {
  type: 'load'
  position: 'top' | 'bottom'
  loadFromTop: LoadMoreCallbackFn
  loadFromBottom: LoadMoreCallbackFn
  numberOfRemainingItems: number
  isCurrentlyLoading?: boolean
  newestFirst?: boolean
}

type EventItem = {type: 'event'} & RolledUpTimelineItem<IssueTimelineItemFragment>
export type TimelineItem = EventItem | LoadItem

/**
 * Temporary type for parsing the timeline items and grouping them into section elements
 * for improve the accessibility of the event items by adding landmarks for navigation.
 */
export type RenderItem = {
  /**
   * Controls whether the item has already been added to the grouped events or not.
   *
   * Only used in events and not in load items, graceful degratation items or issue comments.
   */
  isAddedToGroupedEvents: boolean
  timelineItem: TimelineItem
  render: ReactNode
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const mainTimelineFragment = graphql`
  fragment IssueTimelineIssueFragment on Issue
  @argumentDefinitions(
    skip: {type: "Int"}
    count: {type: "Int", defaultValue: 15}
    itemTypes: {type: "[IssueTimelineItemsItemType!]"}
  ) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useTimelineItemsFrontFragment @arguments(count: $count, skip: $skip, itemTypes: $itemTypes)
    ...useTimelineItemsBackFragment @arguments(itemTypes: $itemTypes)
  }
`

export type IssueTimelineSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>>

type IssueTimelineProps = {
  issue: IssueTimelineIssueFragment$key | null
  /** Callers holding the data outright (the Data Router page) pass it instead of a fragment ref. */
  timelineData?: IssueTimelineIssueFragment$data
  /** Cursor the newest window pins to, derived from the page payload by `timeline-prefill`. */
  initialBackAnchor?: string
  issueBodyRef?: RefObject<HTMLDivElement | null>
  commentsHeadingId?: string
  issueSecondary?: IssueTimelineSecondaryFragment
  highlightedEvent: string | undefined
  page?: number
  sort?: ChronologicalCommentsSort
  // Passed rather than selected: adding them to the fragment changes `IssueViewerViewQuery` and
  // invalidates every cached issue, and the caller already holds all three.
  owner: string
  repo: string
  number: number
  feed?: TimelineFeed
  /** Activity feed only. Must be referentially stable; it keys the timeline's queries. */
  itemTypes?: TimelineItemTypes
  emptyState?: ReactNode
  /** Shown in place of `emptyState` until the timeline is first read. */
  loadingState?: ReactNode
  primaryQueryKey?: readonly unknown[]
  primaryIdentifier?: IssueViewerQueryIdentifier
} & EventProps
export const IssueTimeline = ({
  issue,
  timelineData,
  initialBackAnchor,
  issueBodyRef,
  commentsHeadingId,
  issueSecondary: _issueSecondary,
  viewer,
  highlightedEvent,
  page,
  sort = 'oldest-first',
  owner,
  repo,
  number,
  feed = 'activity',
  itemTypes,
  emptyState,
  loadingState,
  primaryQueryKey,
  primaryIdentifier,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  issueUpdatesChannel,
}: IssueTimelineProps) => {
  const fragmentData = useFragment<IssueTimelineIssueFragment$key>(mainTimelineFragment, issue)
  // The header endpoint is the only thing that refreshes on a transfer, so it outranks the
  // secondary query, which no longer hears about one.
  const sharedHeader = useIssueHeaderDataContext()
  // One of the two is always supplied; the fragment read yields null only for the data-router path.
  const data = (timelineData ?? fragmentData) as IssueTimelineIssueFragment$data
  const timelineProps: TanstackTimelineProps = {
    data,
    issueBodyRef,
    commentsHeadingId,
    owner,
    repo,
    number,
    feed,
    primaryQueryKey,
    primaryIdentifier,
    highlightedEvent,
    page,
    sort,
    emptyState,
    loadingState,
    isTransferInProgress: sharedHeader.isTransferInProgress,
    viewer,
    onCommentChange,
    onCommentReply,
    onCommentEditCancel,
    optionConfig,
    issueUpdatesChannel,
  }

  const endpointTimeline =
    timelineData != null ||
    isFeatureEnabled('issue_viewer_paved_path') ||
    (isFeatureEnabled('issue_viewer_activity_tab') && feed === 'activity')

  if (!endpointTimeline) return <RelayTimeline {...timelineProps} />

  return itemTypes ? (
    // Keyed by filter too: loaded pages and the discovered anchor belong to one filtered timeline.
    <FilteredTimelineFirstPage
      key={`${feed}:${itemTypes.join(',')}`}
      owner={owner}
      repo={repo}
      number={number}
      feed={feed}
      itemTypes={itemTypes}
    >
      <TanstackTimeline {...timelineProps} feed={feed} itemTypes={itemTypes} initialBackAnchor={initialBackAnchor} />
    </FilteredTimelineFirstPage>
  ) : (
    <TanstackTimeline key={feed} {...timelineProps} feed={feed} initialBackAnchor={initialBackAnchor} />
  )
}

// Suspends until a filter's first page loads, so its empty state doesn't show while loading.
function FilteredTimelineFirstPage({
  owner,
  repo,
  number,
  feed,
  itemTypes,
  children,
}: {
  owner: string
  repo: string
  number: number
  feed: TimelineFeed
  itemTypes: TimelineItemTypes
  children: ReactNode
}) {
  const {queryKey, queryFn} = timelineQueryOptions(
    owner,
    repo,
    number,
    frontParamsFor(getInitialTimelinePageSize()),
    feed,
    itemTypes,
  )
  useSuspenseQuery({queryKey, queryFn})

  return children
}

type TimelineSourceProps = {
  data: IssueTimelineIssueFragment$data
  issueBodyRef?: RefObject<HTMLDivElement | null>
  commentsHeadingId?: string
  owner: string
  repo: string
  number: number
  feed: TimelineFeed
  highlightedEvent: string | undefined
  page?: number
  sort: ChronologicalCommentsSort
  emptyState?: ReactNode
  loadingState?: ReactNode
  isTransferInProgress?: boolean
} & EventProps
const RelayTimeline = (props: TimelineSourceProps) => {
  const result = useTimelineItems({
    timelineData: props.data,
    feed: props.feed,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
  })

  return <TimelineItemsPanel {...props} {...result} secondaryTimelineMode="relay" />
}

type TanstackTimelineProps = TimelineSourceProps & {
  itemTypes?: TimelineItemTypes
  initialBackAnchor?: string
  primaryQueryKey?: readonly unknown[]
  primaryIdentifier?: IssueViewerQueryIdentifier
}

const TanstackTimeline = (props: TanstackTimelineProps) => {
  const primaryIdentifier =
    props.primaryIdentifier ??
    (props.optionConfig.accountIssueOption?.isAccountIssue
      ? {permalink: props.optionConfig.accountIssueOption.permalink}
      : {owner: props.owner, repo: props.repo, number: props.number})
  const result = useTimelineItemsTanstack({
    owner: props.owner,
    repo: props.repo,
    number: props.number,
    issueId: props.data.id,
    feed: props.feed,
    itemTypes: props.itemTypes,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
    initialBackAnchor: props.initialBackAnchor,
    primaryQueryKey: props.primaryQueryKey,
    primaryIdentifier,
  })

  return <TimelineItemsPanel {...props} {...result} secondaryTimelineMode="endpoint" />
}

type TimelineItemsPanelProps = TimelineSourceProps &
  UseTimelineItemsCoreResult & {secondaryTimelineMode: 'relay' | 'endpoint'}

const TimelineItemsPanel = ({
  data,
  viewer,
  highlightedEvent,
  page,
  sort,
  emptyState,
  loadingState,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  isTransferInProgress,
  issueBodyRef,
  commentsHeadingId,
  timelineItems,
  totalItemCount,
  isLoading,
  highlightedReady,
  newestSectionQueryKey,
  onCanonicalTimelineUpdated,
  secondaryTimelineMode,
  issueUpdatesChannel,
  owner,
  repo,
  number,
  feed,
}: TimelineItemsPanelProps) => {
  const preheatMode = useRelayCachePreheatMode()
  const orderedTimelineItems = useMemo(
    () => (timelineItems ? orderTimelineItems(timelineItems, sort) : timelineItems),
    [timelineItems, sort],
  )

  return (
    <TimelinePanel
      timelineItems={orderedTimelineItems}
      totalItemCount={totalItemCount}
      isLoading={isLoading}
      newestSectionQueryKey={newestSectionQueryKey}
      onCanonicalTimelineUpdated={onCanonicalTimelineUpdated}
      highlightedEvent={highlightedEvent}
      highlightedReady={highlightedReady}
      issueId={data.id}
      owner={owner}
      repo={repo}
      number={number}
      feed={feed}
      repositoryId={data.repository.id}
      repositoryNameWithOwner={data.repository.nameWithOwner}
      issueUrl={data.url}
      page={page}
      isTransferInProgress={isTransferInProgress}
      viewer={viewer}
      disablePreheat={secondaryTimelineMode === 'endpoint' && preheatMode !== 'tanstack'}
      issueBodyRef={issueBodyRef}
      commentsHeadingId={commentsHeadingId}
      emptyState={emptyState}
      loadingState={loadingState}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
      issueUpdatesChannel={issueUpdatesChannel}
      secondaryTimelineMode={secondaryTimelineMode}
    />
  )
}
